import { Logger } from '@montugroup/utilities/logger';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { viteAppEnv, viteFFOverrides } from '../../constants/env';
const logger = new Logger('useFeatureFlags');
const FF_OVERRIDE_LOCAL_STORAGE_KEY = 'FEATURE_FLAG_OVERRIDES';
const ENV_WITH_FF_OVERRIDES = ['local', 'test', 'development', 'qa', 'staging'];
const getFFOverrides = () => {
    let overrides = {};
    const localStorageFFOverrides = localStorage.getItem(FF_OVERRIDE_LOCAL_STORAGE_KEY);
    const envFFOverrides = viteFFOverrides();
    if (localStorageFFOverrides || envFFOverrides) {
        try {
            const parsedLocalStorageFFOverrides = localStorageFFOverrides ? JSON.parse(localStorageFFOverrides) : {};
            overrides = {
                ...(envFFOverrides ?? {}),
                ...parsedLocalStorageFFOverrides,
            };
            const overridenKeys = Object.entries(overrides).join(' | ');
            logger.info(`Overriding Feature Flags: ${overridenKeys}`);
        }
        catch {
            overrides = {};
        }
    }
    return overrides;
};
/**
 * Builds a portal's `useFeatureFlags` hook from its flag keys and default values.
 * Call it once, at module level, from the portal's `hooks/useFeatureFlags.ts`.
 */
export function createUseFeatureFlags({ defaults, flagKeys }) {
    sessionStorage.setItem('FEATURE_FLAG_KEYS', JSON.stringify(flagKeys, null, 2));
    const shouldApplyFFOverrides = ENV_WITH_FF_OVERRIDES.includes(viteAppEnv());
    const featureFlagOverrides = getFFOverrides();
    /**
     * Returns the fully merged feature flag object.
     *
     * @deprecated 🚫 Prefer `useFeatureFlag` or `useFeatureFlagsSelector` with a selector instead.
     *
     * This hook combines:
     * - default flag values (`defaults`)
     * - remote flag values (`useFlags()`)
     * - optional runtime overrides (`featureFlagOverrides`)
     *
     * ⚠️ Subscription behavior:
     * This hook will rerender whenever *any* feature flag value changes,
     * because it subscribes to the entire flag set.
     *
     * ✅ Use this hook when:
     * - you need access to many or all flags
     * - building higher-level abstractions (e.g., selector hooks)
     * - performing one-time reads inside effects or event handlers
     *
     * 🚫 Avoid using this directly in leaf UI components that only depend
     * on a small number of flags, as it can cause unnecessary rerenders.
     *
     * @returns The full set of merged feature flags.
     */
    return function useFeatureFlags() {
        return {
            flags: {
                ...defaults,
                ...useFlags(),
                ...(shouldApplyFFOverrides ? featureFlagOverrides : {}),
            },
        };
    };
}
//# sourceMappingURL=data:application/json;base64,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